import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Sticky area at the bottom, e.g. a composer. */
  footer?: ReactNode;
  widthClassName?: string;
}

/**
 * Right-side panel used by the AI assistant.
 * Closes on backdrop click and on Escape, and locks page scroll while open.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  widthClassName = 'w-[420px]',
}: DrawerProps) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 animate-kainos-fade-in bg-[rgba(15,23,42,0.35)]"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        className={cn(
          'fixed right-0 top-0 z-50 flex h-screen animate-kainos-slide-in flex-col bg-surface shadow-drawer',
          'max-w-full',
          widthClassName,
        )}
      >
        <header className="flex items-center justify-between border-b border-line px-5 py-[18px]">
          {title}
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="p-1 text-ink-faint transition-colors hover:text-ink-body"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto p-5">{children}</div>

        {footer ? <div className="border-t border-line px-4 py-3.5">{footer}</div> : null}
      </aside>
    </>
  );
}
